import _ from 'lodash'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'

import PageContainer from '@/components/ui/containers/PageContainer'

import OrdersTable from '@/pages/meeting-link/containers/OrdersTable'

import { OrderStatus } from '@/core/models/order.model'
import { useGetMeetingLinksQuery } from '@/core/services/meeting-link'
import { LIST_ENDPOINT_MAX_LIMIT } from '@/core/utils/list-response.util'
import { useGetMeetingLinkOrdersQuery } from '@/core/services/order'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { isStaff } from '@/utils/type-guards'

type StatusTab = OrderStatus | 'all'

/**
 * TODO: show other orders beside meeting link order ?
 */
export default function OrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const [tab, setTab] = useState<StatusTab>((searchParams.get('status') as OrderStatus | null) ?? 'all')

  const status = useMemo(() => {
    switch (tab) {
      case 'all':
        return undefined
      case OrderStatus.Pending:
        if (isStaff(authUser) && authUser?.status === 'MEDECIN') {
          return [OrderStatus.Pending, OrderStatus.Accepted]
        }
        return [tab]
      default:
        return [tab]
    }
  }, [authUser, tab])

  const { data: meetingLinksResponse } = useGetMeetingLinksQuery(
    { staffs: authUser ? [authUser._id] : [], limit: LIST_ENDPOINT_MAX_LIMIT },
    { skip: !authUser }
  )
  const meetingLinks = meetingLinksResponse?.items
  const { data: meetingLinkOrders } = useGetMeetingLinkOrdersQuery({ meetingLinks: _.map(meetingLinks, '_id'), status }, { skip: !meetingLinks })

  const statusTabs = useMemo(
    () =>
      _.reject(
        [
          { label: t('page.orders.statusTab.all'), value: 'all' },
          { label: t(`page.orders.statusTab.${_.camelCase(OrderStatus.Pending)}`), value: OrderStatus.Pending },
          { label: t(`page.orders.statusTab.${_.camelCase(OrderStatus.Accepted)}`), value: OrderStatus.Accepted },
          { label: t(`page.orders.statusTab.${_.camelCase(OrderStatus.Refused)}`), value: OrderStatus.Refused },
          { label: t(`page.orders.statusTab.${_.camelCase(OrderStatus.Canceled)}`), value: OrderStatus.Canceled },
        ],
        _.isNil
      ) as Array<{ label: string; value: string }>,
    [t]
  )

  const handleChangeTab = (_event: React.SyntheticEvent, newTab: StatusTab) => {
    if (searchParams.has('status')) {
      searchParams.delete('status')
      setSearchParams(searchParams.toString())
    }

    setTab(newTab)
  }

  return (
    <PageContainer>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
        <Typography fontSize={{ xs: '1.25rem', md: '1.5rem' }} fontWeight={700} variant="h4" gutterBottom>
          {t('page.orders.title')}
        </Typography>
      </Stack>
      <Card>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            boxShadow: 'rgba(145, 158, 171, 0.08) 0px -2px 0px 0px inset',
            pl: '20px',
            pr: '20px',
          }}
        >
          {statusTabs.map((tab) => (
            <Tab key={tab.value} disableRipple label={tab.label} value={tab.value} sx={{ textTransform: 'initial' }} />
          ))}
        </Tabs>
        <OrdersTable key={tab} orders={meetingLinkOrders} totalCount={meetingLinkOrders?.length} />
      </Card>
    </PageContainer>
  )
}
